import { IApiResponse } from '@interfaces/module.interface';
import {
  CreateUpdateDiscountPayload,
  DiscountsInterface,
} from '@modules/point-of-sale/interface/bo-interface/discount-list.interface';

import { api } from '@services/api';
import { handleError } from '@utils/api-error-handling';
import { encryptPayload } from '@utils/security';

// #region API Endpoints
export const discountListAPI = api
  .enhanceEndpoints({
    addTagTypes: ['DiscountList', 'DiscountDetails'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getAllDiscountList: builder.query({
        query: (params) => {
          const filterIsActive = params?.filter?.isActive;

          return `discounts/?filter[isActive]=${filterIsActive}`;
        },
        providesTags: ['DiscountList'],
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: (response: IApiResponse<DiscountsInterface>) =>
          response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      getAllDiscountListWithParams: builder.query({
        query: (params) => {
          const discountID = params?.discountID;
          const filterIsActive = params?.filter?.isActive;

          return `discounts/${discountID}?filter[isActive]=${filterIsActive}`;
        },
        providesTags: ['DiscountDetails'],
        async onQueryStarted(_arg, { queryFulfilled }) {
          await queryFulfilled;
        },
        transformResponse: (response: IApiResponse<DiscountsInterface>) =>
          response?.data,
        forceRefetch: () => true,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      createDiscount: builder.mutation({
        query: (payload: CreateUpdateDiscountPayload) => ({
          url: 'discounts',
          method: 'POST',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            if (data) {
              dispatch(discountListAPI.util.invalidateTags(['DiscountList']));
            }
          } catch (error) {}
        },
        transformResponse: (response: {
          data: {
            referenceIDs: string[];
            response: { responseCode: number; responseMessage: string }[];
          };
        }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
      editDiscount: builder.mutation({
        query: ({
          refId,
          payload,
        }: {
          refId: string;
          payload: CreateUpdateDiscountPayload;
        }) => ({
          url: `discounts/${refId}`,
          method: 'PUT',
          body: encryptPayload(payload),
        }),
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(discountListAPI.util.invalidateTags(['DiscountList']));
          }
        },
        transformResponse: (response: {
          data: {
            referenceIDs: string[];
            response: { responseCode: number; responseMessage: string }[];
          };
        }) => response.data,
        transformErrorResponse: (baseQueryReturnValue) =>
          handleError(baseQueryReturnValue),
      }),
    }),
  });
// #endregion API Endpoints

// #region Export Hooks
export const {
  useGetAllDiscountListQuery,
  useGetAllDiscountListWithParamsQuery,
  useCreateDiscountMutation,
  useEditDiscountMutation,
} = discountListAPI;
// #endregion Export Hooks
